import React, { useState, useRef, useContext } from "react";
import {
  Button,
  Carousel,
  Popconfirm,
  Tooltip,
  message,
} from "antd";
import { LeftCircleFilled, RightCircleFilled } from "@ant-design/icons";
import moment from "moment";
import { dateFormat } from "../../Tasks/configs/designTokens";
import MediaFetcher from "../library/MediaFetcher";
import { LibraryCreativesContext } from "../../../../core/components/creatives/library/LibraryCreativesProvider";
import { useCreativeNavigation } from "../hooks/useCreativeNavigation";

const PrevArrow = ({ className, onClick }) => (
  <LeftCircleFilled
    className={`${className} creative-detail-card__arrow`}
    onClick={onClick}
    aria-label="Previous slide"
  />
);

const NextArrow = ({ className, onClick }) => (
  <RightCircleFilled
    className={`${className} creative-detail-card__arrow`}
    onClick={onClick}
    aria-label="Next slide"
  />
);

const MetricBlock = ({ label, value, large, muted }) => (
  <div className="creative-metric-block">
    <span className="creative-metric-block__label">{label}</span>
    <span
      className={`creative-metric-block__value${
        large ? " creative-metric-block__value--large" : ""
      }${muted ? " creative-metric-block__value--muted" : ""}`}
    >
      {value}
    </span>
  </div>
);

const AdGroupHeroCard = ({
  adgroupinfo,
  mediaType = "DISPLAY",
  mediaKeys = [],
  onDeleted,
}) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const { navigateBack } = useCreativeNavigation();
  const { deleteAdGroupCreatives } = useContext(LibraryCreativesContext);

  const adGroup = adgroupinfo?.adGroupCreatives;

  const carouselRef = useRef(null);

  const handleBeforeChange = (from, to) => {
    // Pause all videos in the carousel to ensure the previous one stops
    const container = carouselRef.current;
    if (container) {
      const videos = container.querySelectorAll('video');
      videos.forEach(video => video.pause());
    }
    setCurrentSlide(to);
  };

  const handleDelete = async () => {
    try {
      await deleteAdGroupCreatives({
        variables: { id: adGroup.id },
      });
      message.success("Ad group deleted successfully.");
      if (onDeleted) onDeleted();
      navigateBack({ mediaType, isAdGroup: true });
    } catch (err) {
      message.error("Failed to delete ad group.");
    }
  };

  const adUnitCount = adGroup?.medias?.length ?? 0;
  const updatedAt = adGroup?.updatedAt
    ? moment(adGroup.updatedAt).format(dateFormat.full)
    : "—";
  const createdAt = adGroup?.createdAt
    ? moment(adGroup.createdAt).format(dateFormat.full)
    : "—";

  return (
    <div className="creative-hero-card">
      {/* Action bar */}
      <div className="creative-hero-card__action-bar">
        <Popconfirm
          placement="topLeft"
          title="Are you sure you want to delete this ad group? This action cannot be undone."
          onConfirm={handleDelete}
          okText="Delete"
          cancelText="Cancel"
          overlayClassName="creative-popconfirm"
          okButtonProps={{}}
          cancelButtonProps={{}}
        >
          <Tooltip title="Delete this ad group">
            <Button type="default" danger>
              Delete Ad Group
            </Button>
          </Tooltip>
        </Popconfirm>
      </div>

      {/* Grid body: preview | metrics */}
      <div className="creative-hero-card__body">
        {/* Dark preview area */}
        <div className="creative-hero-card__preview" ref={carouselRef}>
          {mediaKeys.length > 0 ? (
            <>
              <Carousel
                arrows
                prevArrow={<PrevArrow />}
                nextArrow={<NextArrow />}
                beforeChange={handleBeforeChange}
                dots={false}
              >
                {mediaKeys.map((key, index) => (
                  <div key={index}>
                    <MediaFetcher
                      mediaKey={key}
                      className="creative-hero-card__media"
                    />
                  </div>
                ))}
              </Carousel>
              <div className="creative-hero-card__counter">
                Ad Unit {currentSlide + 1} of {mediaKeys.length}
              </div>
            </>
          ) : (
            <span className="creative-hero-card__empty-text">No media</span>
          )}
        </div>

        {/* Metrics panel */}
        <div className="creative-hero-card__metrics">
          <div className="creative-hero-card__metrics-grid">
            <MetricBlock
              label="Ad Units"
              value={adUnitCount}
              large
            />
            <MetricBlock label="Last Updated" value={updatedAt} />
            <MetricBlock label="Created On" value={createdAt} />
          </div>


        </div>
      </div>
    </div>
  );
};

export default AdGroupHeroCard;
